import * as React from 'react'
import { Loader2, Plus, X } from 'lucide-react'
import type { IrrigationProblemTypeDto } from '@lib/dto/irrigation-problem-type.dto'
import {
  IRRIGATION_PROBLEM_SUBJECTS,
  IRRIGATION_PROBLEM_SUBJECT_LABELS,
  type IrrigationProblemSubject,
} from '@lib/enums/irrigation.enum'
import { createIrrigationProblemTypeInputSchema } from '@lib/input/irrigation-problem-type.input'
import {
  useArchiveIrrigationProblemType,
  useCreateIrrigationProblemType,
  useIrrigationProblemTypes,
  useUpdateIrrigationProblemType,
} from '@/hooks/useIrrigationProblemTypes'
import { PartPicker } from '@/components/PartPicker'
import { ProblemTypeVideo } from '@/components/ProblemTypeVideo'
import { StockPhotoField, StockPhotoThumb } from '@/components/StockPhotoField'
import { useArchiveVideo } from '@/hooks/useVideos'
import { Button } from '@/components/ui/button'
import logger, { errorMessage, toErrorMessage } from '@/helpers/logger'
import { cn } from '@/lib/utils'

const INPUT_CLASS =
  'h-8 w-full rounded-md border border-default bg-surface px-2 text-sm text-primary-text transition-colors hover:border-strong focus-visible:outline-none'
const TEXTAREA_CLASS =
  'min-h-20 w-full rounded-md border border-default bg-surface px-2 py-1.5 text-sm text-primary-text transition-colors hover:border-strong focus-visible:outline-none'

/** "A spot in a zone, A valve", for a row that has no room for more. */
function describeAppliesTo(
  subjects: readonly IrrigationProblemSubject[],
): string {
  return subjects
    .map((subject) => IRRIGATION_PROBLEM_SUBJECT_LABELS[subject])
    .join(', ')
}

/** "Usual repair: 20 min · 2 parts", or a nudge when there is none yet. */
function describeRepair(entry: IrrigationProblemTypeDto): string {
  const parts: string[] = []
  if (entry.defaultLaborMinutes !== null)
    parts.push(`${String(entry.defaultLaborMinutes)} min`)
  const count = entry.defaultMaterials.length
  if (count > 0)
    parts.push(`${String(count)} ${count === 1 ? 'part' : 'parts'}`)
  return parts.length === 0
    ? 'No usual repair yet'
    : `Usual repair: ${parts.join(' · ')}`
}

interface DraftMaterial {
  uuid: string
  name: string
  quantity: string
}

interface ProblemDraft {
  name: string
  description: string
  appliesTo: IrrigationProblemSubject[]
  defaultLaborMinutes: string
  defaultMaterials: DraftMaterial[]
  photoDocumentId: string | null
  videoId: string | null
}

function toDraft(entry: IrrigationProblemTypeDto | null): ProblemDraft {
  if (entry === null) {
    return {
      name: '',
      description: '',
      appliesTo: [],
      defaultLaborMinutes: '',
      defaultMaterials: [],
      photoDocumentId: null,
      videoId: null,
    }
  }
  return {
    name: entry.name,
    description: entry.description ?? '',
    appliesTo: [...entry.appliesTo],
    defaultLaborMinutes:
      entry.defaultLaborMinutes === null
        ? ''
        : String(entry.defaultLaborMinutes),
    defaultMaterials: entry.defaultMaterials.map((item) => ({
      uuid: item.material.uuid,
      name: item.material.name,
      quantity: item.quantity,
    })),
    photoDocumentId: entry.photoDocumentId,
    videoId: entry.videoId,
  }
}

/**
 * The draft as the API takes it, validated by the shared schema so the
 * message is the one the server would give, before a round trip.
 */
function toInput(draft: ProblemDraft) {
  const minutes = draft.defaultLaborMinutes.trim()
  return createIrrigationProblemTypeInputSchema.safeParse({
    name: draft.name,
    description: draft.description,
    // Kept in the directory's own order, whatever order they were ticked in.
    appliesTo: IRRIGATION_PROBLEM_SUBJECTS.filter((subject) =>
      draft.appliesTo.includes(subject),
    ),
    defaultLaborMinutes: minutes === '' ? null : Number(minutes),
    defaultMaterials: draft.defaultMaterials.map((item) => ({
      materialUuid: item.uuid,
      quantity: item.quantity,
    })),
    photoDocumentId: draft.photoDocumentId,
    videoId: draft.videoId,
  })
}

/**
 * The irrigation problem directory: what a technician can record, what a
 * client is told about it, and the repair it usually takes.
 *
 * The usual repair is a starting point the technician adjusts on each
 * problem (three nozzles, not one), not a fixed price.
 */
export function IrrigationProblemSettings() {
  const problemTypes = useIrrigationProblemTypes()
  const createType = useCreateIrrigationProblemType()
  const updateType = useUpdateIrrigationProblemType()
  const archiveType = useArchiveIrrigationProblemType()
  const [isAdding, setIsAdding] = React.useState(false)
  const [editingId, setEditingId] = React.useState<string | null>(null)
  const [error, setError] = React.useState<string | null>(null)

  if (problemTypes.isPending) {
    return (
      <section className="rounded-lg border border-default bg-surface p-4">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-muted-text" />
        </div>
      </section>
    )
  }

  if (problemTypes.isError) {
    return (
      <section className="rounded-lg border border-default bg-surface p-4 text-center">
        <p className="text-sm font-medium">
          Irrigation problems could not be loaded
        </p>
        <p className="mt-1 text-sm text-muted-text">
          Check your connection and try again.
        </p>
      </section>
    )
  }

  const rows = problemTypes.data

  async function handleArchiveToggle(
    entry: IrrigationProblemTypeDto,
  ): Promise<void> {
    setError(null)
    try {
      await archiveType.mutateAsync({
        id: entry.id,
        archived: entry.archivedAt === null,
      })
    } catch (caught) {
      setError(errorMessage(caught, 'That could not be saved.'))
    }
  }

  return (
    <section className="space-y-3 rounded-lg border border-default bg-surface p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Irrigation problems</h2>
          <p className="mt-0.5 text-sm text-muted-text">
            What a technician can record on a sprinkler system, and what a
            client is told about it. The description appears in proposals, so
            write it in plain language. The usual repair is where each problem
            starts; the technician adjusts it in the field.
          </p>
        </div>
        {isAdding ? null : (
          <Button
            variant="outline"
            size="sm"
            className="shrink-0"
            onClick={() => {
              setError(null)
              setEditingId(null)
              setIsAdding(true)
            }}
          >
            <Plus className="h-3.5 w-3.5" />
            Add problem
          </Button>
        )}
      </div>

      {error === null ? null : <p className="text-sm text-danger">{error}</p>}

      {isAdding ? (
        <div className="rounded-md border border-default bg-subtle px-3 py-3">
          <ProblemForm
            initial={toDraft(null)}
            isSaving={createType.isPending}
            submitLabel="Add problem"
            onCancel={() => {
              setIsAdding(false)
            }}
            onSubmit={async (draft) => {
              setError(null)
              const parsed = toInput(draft)
              if (!parsed.success) {
                setError(parsed.error.issues[0]?.message ?? 'Check the details')
                return
              }
              try {
                await createType.mutateAsync(parsed.data)
                setIsAdding(false)
              } catch (caught) {
                setError(errorMessage(caught, 'That could not be saved.'))
              }
            }}
          />
        </div>
      ) : null}

      {rows.length === 0 ? (
        <p className="rounded-md border border-default px-3 py-6 text-center text-sm text-muted-text">
          No irrigation problems catalogued yet. A technician can still record
          one and describe it themselves; what is listed here is what they can
          pick without typing.
        </p>
      ) : (
        <ul className="divide-y divide-default rounded-md border border-default">
          {rows.map((entry) => (
            <li key={entry.id}>
              <div
                className={cn(
                  'group flex items-center gap-3 px-3 py-2',
                  entry.archivedAt === null ? null : 'opacity-55',
                )}
              >
                <StockPhotoThumb documentId={entry.photoDocumentId} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">
                    {entry.name}
                    {entry.archivedAt === null ? null : (
                      <span className="ml-2 text-xs font-normal text-muted-text">
                        Archived
                      </span>
                    )}
                  </span>
                  <span className="mt-0.5 block truncate text-xs text-muted-text">
                    {describeAppliesTo(entry.appliesTo)} ·{' '}
                    {describeRepair(entry)}
                    {entry.description === null
                      ? ' · No client description yet'
                      : null}
                  </span>
                </span>
                {entry.problemCount === 0 ? null : (
                  <span className="shrink-0 text-xs text-muted-text">
                    Recorded {entry.problemCount}{' '}
                    {entry.problemCount === 1 ? 'time' : 'times'}
                  </span>
                )}
                {entry.archivedAt === null ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setError(null)
                      setIsAdding(false)
                      setEditingId(editingId === entry.id ? null : entry.id)
                    }}
                  >
                    Edit
                  </Button>
                ) : null}
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={archiveType.isPending}
                  onClick={() => {
                    void handleArchiveToggle(entry)
                  }}
                >
                  {entry.archivedAt === null ? 'Archive' : 'Restore'}
                </Button>
              </div>
              {editingId === entry.id ? (
                <div className="border-t border-default bg-subtle px-3 py-3">
                  <ProblemForm
                    initial={toDraft(entry)}
                    isSaving={updateType.isPending}
                    submitLabel="Save changes"
                    onCancel={() => {
                      setEditingId(null)
                    }}
                    onSubmit={async (draft) => {
                      setError(null)
                      const parsed = toInput(draft)
                      if (!parsed.success) {
                        setError(
                          parsed.error.issues[0]?.message ??
                            'Check the details',
                        )
                        return
                      }
                      try {
                        await updateType.mutateAsync({
                          id: entry.id,
                          input: parsed.data,
                        })
                        setEditingId(null)
                      } catch (caught) {
                        setError(errorMessage(caught, 'That could not be saved.'))
                      }
                    }}
                  />
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

interface ProblemFormProps {
  initial: ProblemDraft
  isSaving: boolean
  submitLabel: string
  onCancel: () => void
  onSubmit: (draft: ProblemDraft) => Promise<void>
}

function ProblemForm({
  initial,
  isSaving,
  submitLabel,
  onCancel,
  onSubmit,
}: ProblemFormProps) {
  const [draft, setDraft] = React.useState<ProblemDraft>(initial)
  const [isPicking, setIsPicking] = React.useState(false)
  const [uploading, setUploading] = React.useState(false)
  const discardVideo = useArchiveVideo(null)

  function toggleSubject(
    subject: IrrigationProblemSubject,
    checked: boolean,
  ): void {
    setDraft({
      ...draft,
      appliesTo: checked
        ? [...draft.appliesTo, subject]
        : draft.appliesTo.filter((entry) => entry !== subject),
    })
  }

  function setQuantity(uuid: string, quantity: string): void {
    setDraft({
      ...draft,
      defaultMaterials: draft.defaultMaterials.map((item) =>
        item.uuid === uuid ? { ...item, quantity } : item,
      ),
    })
  }

  return (
    <form
      className="space-y-3"
      onSubmit={(event) => {
        event.preventDefault()
        void onSubmit(draft)
      }}
    >
      <div className="space-y-1">
        <label
          htmlFor="irrigation-problem-name"
          className="text-sm font-medium"
        >
          Name
        </label>
        <input
          id="irrigation-problem-name"
          className={INPUT_CLASS}
          value={draft.name}
          placeholder="e.g. Bad nozzle"
          onChange={(event) => {
            setDraft({ ...draft, name: event.target.value })
          }}
        />
      </div>

      <div className="space-y-1">
        <label
          htmlFor="irrigation-problem-description"
          className="text-sm font-medium"
        >
          What it is, for a client
        </label>
        <textarea
          id="irrigation-problem-description"
          className={TEXTAREA_CLASS}
          value={draft.description}
          placeholder="e.g. A clogged or broken nozzle throws water in the wrong pattern, leaving dry spots and wetting walks."
          onChange={(event) => {
            setDraft({ ...draft, description: event.target.value })
          }}
        />
      </div>

      <fieldset className="space-y-1">
        <legend className="text-sm font-medium">Recorded on</legend>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 sm:grid-cols-3">
          {IRRIGATION_PROBLEM_SUBJECTS.map((subject) => (
            <label key={subject} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="h-4 w-4"
                checked={draft.appliesTo.includes(subject)}
                onChange={(event) => {
                  toggleSubject(subject, event.target.checked)
                }}
              />
              {IRRIGATION_PROBLEM_SUBJECT_LABELS[subject]}
            </label>
          ))}
        </div>
      </fieldset>

      <div className="space-y-1">
        <p className="text-sm font-medium">Usual repair</p>
        <label className="flex items-center gap-2 text-sm">
          <input
            className={cn(INPUT_CLASS, 'w-20')}
            inputMode="numeric"
            value={draft.defaultLaborMinutes}
            aria-label="Usual labor, in minutes"
            onChange={(event) => {
              setDraft({ ...draft, defaultLaborMinutes: event.target.value })
            }}
          />
          minutes of labor
        </label>
        {draft.defaultMaterials.length === 0 ? null : (
          <ul className="divide-y divide-default rounded-md border border-default bg-surface">
            {draft.defaultMaterials.map((item) => (
              <li key={item.uuid} className="flex items-center gap-2 px-2 py-1">
                <input
                  className={cn(INPUT_CLASS, 'h-7 w-16')}
                  inputMode="decimal"
                  value={item.quantity}
                  aria-label={`Quantity of ${item.name}`}
                  onChange={(event) => {
                    setQuantity(item.uuid, event.target.value)
                  }}
                />
                <span className="min-w-0 flex-1 truncate text-sm">
                  {item.name}
                </span>
                <button
                  type="button"
                  className="rounded p-1 text-muted-text hover:bg-subtle hover:text-primary-text"
                  aria-label={`Remove ${item.name}`}
                  onClick={() => {
                    setDraft({
                      ...draft,
                      defaultMaterials: draft.defaultMaterials.filter(
                        (entry) => entry.uuid !== item.uuid,
                      ),
                    })
                  }}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}
        {isPicking ? (
          <PartPicker
            autoFocus
            excludeUuids={draft.defaultMaterials.map((item) => item.uuid)}
            onPick={(part) => {
              setDraft({
                ...draft,
                defaultMaterials: [
                  ...draft.defaultMaterials,
                  { uuid: part.uuid, name: part.name, quantity: '1' },
                ],
              })
              setIsPicking(false)
            }}
          />
        ) : (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              setIsPicking(true)
            }}
          >
            <Plus className="h-3.5 w-3.5" />
            Add a part
          </Button>
        )}
      </div>

      <StockPhotoField
        documentId={draft.photoDocumentId}
        onChange={(photoDocumentId) => {
          // Functional: this lands after an upload, and the person may have
          // gone on editing meanwhile.
          setDraft((current) => ({ ...current, photoDocumentId }))
        }}
        onBusyChange={setUploading}
        hint="Stands in on a proposal when the technician could not photograph the problem itself."
      />

      <ProblemTypeVideo
        videoId={draft.videoId}
        onChange={(videoId) => {
          setDraft((current) => ({ ...current, videoId }))
        }}
      />

      <div className="flex items-center gap-2 border-t border-default pt-3">
        <Button type="submit" disabled={isSaving || uploading}>
          {isSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
          {submitLabel}
        </Button>
        <Button
          type="button"
          variant="ghost"
          onClick={() => {
            // A clip recorded into this draft and never saved points at
            // nothing; Stream bills for it until the sweep finds it a day on.
            if (draft.videoId !== null && draft.videoId !== initial.videoId) {
              void discardVideo.mutateAsync(draft.videoId).catch((caught) => {
                logger.error('Could not discard an unsaved explainer video', {
                  message: toErrorMessage(caught),
                })
              })
            }
            onCancel()
          }}
        >
          Cancel
        </Button>
      </div>
    </form>
  )
}
